//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { createContext, type Dispatch, type SetStateAction } from 'react';
import { type UserProfile } from '@/services/apiTypes';

// -----------------------------------------------------------------------
// Auth context value.
//
// Holds the reactive auth state (activeUser, activeUserAuthMethod,
// userProfile, authStatus) plus the pure auth methods and the session-state
// reads (xgtConnectionInfo).  The login orchestration (login/oidcLogin/
// getUserProfile) lives in useAuthActions and consumes the setters here.
// -----------------------------------------------------------------------

export type AuthStatus = 'pending' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  // --- State ---
  activeUser: string | null;
  setActiveUser: Dispatch<SetStateAction<string | null>>;
  activeUserAuthMethod: string;
  setActiveUserAuthMethod: Dispatch<SetStateAction<string>>;
  userProfile: UserProfile | null;
  setUserProfile: Dispatch<SetStateAction<UserProfile | null>>;

  // Reactive auth-resolution status, derived from whether the startup session
  // restore has settled and whether it yielded a user.  Drives the route gate
  // (ProtectedRoute) reactively, replacing the old non-reactive userLoaded flag.
  authStatus: AuthStatus;
  // Called once by the startup orchestration (AppBootstrap) when the initial
  // session restore settles, flipping authStatus off 'pending'.
  markAuthResolved: () => void;

  // --- Controller-owned reads (non-reactive snapshots) ---
  xgtConnectionInfo: { host: string; port: number };

  // --- Methods ---
  // broadcast (default true) tells other tabs of this browser to log out too;
  // pass false when the logout was itself triggered by another tab's broadcast.
  logout: (broadcast?: boolean) => Promise<void>;
  restoreSession: () => Promise<void>;
  isAuthenticated: () => boolean;
  getSessionExpiredTime: () => number | null;
  setRestoreSessionEnabled: (v: boolean) => void;
  clearSessionExpired: () => void;
  getOidcAuthorizationUrl: (
    host: string, port: number | string
  ) => Promise<{ url: string }>;
  updateUserProfile: (profile: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export { AuthContext };
